import React from 'react';
import toast from 'react-hot-toast';
import { STATUS_CONFIG, ROLE_NAMES } from './constants';

/**
 * Format date to readable string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

/**
 * Format datetime to readable string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(date);
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  return formatDate(dateString);
};

/**
 * Get status configuration
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
};

/**
 * Get role display name
 */
export const getRoleName = (role) => {
  return ROLE_NAMES[role] || role;
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get initials from name
 */
export const getInitials = (name) => {
  if (!name) return '';
  const names = name.split(' ');
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get random color for avatar
 */
export const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if file is valid image
 */
export const isValidImage = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * Check if file is valid video
 */
export const isValidVideo = (file) => {
  const validTypes = ['video/mp4', 'video/mpeg', 'video/quicktime'];
  return validTypes.includes(file.type);
};

/**
 * Check if file is valid media (image or video)
 */
export const isValidMedia = (file) => {
  return isValidImage(file) || isValidVideo(file);
};

/**
 * Handle API error and return user-friendly message
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error
    const errorData = error.response.data;
    let errorMessage = errorData?.error || errorData?.message || errorData?.error_en || 'An error occurred';
    
    // Add more details if available
    if (errorData?.details) {
      errorMessage += `: ${errorData.details}`;
    }
    if (errorData?.message_en && errorMessage === errorData.message_en) {
      // If message_en exists, prefer it
      errorMessage = errorData.message_en;
      if (errorData?.requested_platform || errorData?.requested_content_type) {
        errorMessage += ` (Platform: ${errorData.requested_platform || 'N/A'}, Content Type: ${errorData.requested_content_type || 'N/A'})`;
      }
    }
    
    return errorMessage;
  } else if (error.request) {
    // Request made but no response
    return 'No response from server. Please check your connection.';
  } else {
    // Something else happened
    return error.message || 'An unexpected error occurred';
  }
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

/**
 * Download file from URL
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get full image URL (handles relative paths)
 */
export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Relative path - prepend API base URL
  let apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  
  // Remove trailing /api if present to avoid double /api/api
  apiBase = apiBase.replace(/\/api\/?$/, '');
  
  // If URL already starts with /api, use it as is
  // Otherwise, prepend the base URL
  if (url.startsWith('/api/')) {
    return `${apiBase}${url}`;
  }
  
  return `${apiBase}${url.startsWith('/') ? url : '/' + url}`;
};

/**
 * Get media type from URL (image or video)
 */
export const getMediaTypeFromUrl = (url) => {
  if (!url) return 'image';
  const videoExtensions = ['.mp4', '.mov', '.mpeg', '.avi', '.webm'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext)) ? 'video' : 'image';
};

/**
 * Toast notification helpers
 */
export const showToast = {
  success: (message) => {
    toast.success(message, {
      duration: 3000,
    });
  },
  error: (message) => {
    toast.error(message, {
      duration: 4000,
    });
  },
  loading: (message) => {
    return toast.loading(message);
  },
  promise: (promise, messages) => {
    return toast.promise(promise, messages);
  },
  dismiss: (toastId) => {
    toast.dismiss(toastId);
  },
};

/**
 * Confirm dialog using toast (with custom confirm/cancel buttons)
 * Returns a Promise that resolves to true if confirmed, false otherwise
 */
export const showConfirm = (message, isRTL = false) => {
  return new Promise((resolve) => {
    const confirmId = toast(
      (t) => (
        <div className="flex flex-col gap-3">
          <p className="font-medium">{message}</p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
            >
              {isRTL ? 'تأكيد' : 'Confirm'}
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
      }
    );
  });
};


